/* What has happened that concerns me.
 *
 * Read out of Frappe's own Notification Log rather than kept as a second
 * list: assignments, mentions, shares and whatever a tenant's own alerts
 * produce all land there already, and two lists would disagree within a week.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useErp } from '@jackdesk/erp-kit'
import type { Notice } from '@jackdesk/erp-kit'

export function Notices() {
  const erp = useErp()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const [items, setItems] = useState<Notice[]>([])
  const [unread, setUnread] = useState(0)
  const box = useRef<HTMLDivElement>(null)

  const load = useCallback(() => {
    erp.notifications()
      .then((r) => { setItems(r.notifications); setUnread(r.unread) })
      .catch(() => { /* a bell that breaks the page is worse than no bell */ })
  }, [erp])

  useEffect(() => {
    load()
    const timer = window.setInterval(load, 60_000)
    return () => window.clearInterval(timer)
  }, [load])

  return (
    <div style={{ position: 'relative' }} ref={box}>
      <button className="btn btn-quiet" aria-expanded={open}
              aria-label={unread ? `${unread} things to look at` : 'Nothing new'}
              onClick={() => { setOpen((v) => !v); if (!open) load() }}>
        <span aria-hidden="true">🔔</span>
        {unread ? <span className="bell-count" aria-hidden="true">{unread}</span> : null}
      </button>

      {open ? (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 29 }} onClick={() => setOpen(false)} />
          {/* Anchored to the bell and kept inside the window. It used to sit
              at the foot of the side navigation, which is `overflow: hidden`
              and the full height of the screen, so this opened below the
              bottom edge and was clipped away — the bell appeared to do
              nothing at all. It lives in the top bar now, and the width is
              capped so a 390px screen gets no sideways scroll. */}
          <div className="card" role="dialog" aria-label="Things to look at" style={{
            position: 'absolute', right: 0, top: 'calc(100% + 6px)', zIndex: 30,
            width: 'min(340px, calc(100vw - 32px))',
            maxHeight: 'min(420px, calc(100vh - 80px))',
            overflowY: 'auto', boxShadow: 'var(--shadow-2)',
          }}>
            <div className="row-between" style={{ padding: '8px 10px' }}>
              <strong className="small">Things to look at</strong>
              {unread ? (
                <button className="linkish small" onClick={async () => {
                  await erp.markNoticesRead(); load()
                }}>Mark all read</button>
              ) : null}
            </div>
            {items.length ? items.map((n) => (
              <button key={n.id} className="navitem" style={{ width: '100%', textAlign: 'left' }}
                      onClick={async () => {
                        if (!n.read) await erp.markNoticesRead([n.id])
                        setOpen(false)
                        if (n.doctype && n.name) {
                          navigate(`/doc/${encodeURIComponent(n.doctype)}/${encodeURIComponent(n.name)}`)
                        }
                        load()
                      }}>
                <span style={{ fontWeight: n.read ? 400 : 600 }}>{n.subject}</span>
                {n.doctype ? <span className="small muted"> · {n.doctype}</span> : null}
              </button>
            )) : (
              <p className="small muted" style={{ padding: '4px 10px 10px', margin: 0 }}>
                Nothing yet. Assignments and mentions turn up here.
              </p>
            )}
          </div>
        </>
      ) : null}
    </div>
  )
}
